import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimerIcon from '@mui/icons-material/Timer';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { getVoiceForMessage } from '../../utils/voiceUtils';
import { markdownToSSML, containsMarkdown } from '../../utils/markdownToSSML';
import AudioExportDialog from './AudioExportDialog';
import api from '../../services/api';
import SocketTTSStream from '../../services/SocketTTSStream';
import { useSocket } from '../../context/SocketContext';

const ConversationAutoplay = ({
  messages,
  userSettings,
  platformSettings,
  therapistSettings,
  clientId,
  conversationId,
  onMessageHighlight,
  onClose,
  hideExportOption = false,
  token
}) => {
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Settings state
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [pauseBetweenMessages, setPauseBetweenMessages] = useState(1500); // 1.5 seconds default
  const [skipSystemMessages, setSkipSystemMessages] = useState(true);
  const [autoScrollToMessage, setAutoScrollToMessage] = useState(true);
  
  // Provider detection for optimized performance
  const [ttsProvider, setTtsProvider] = useState(null);
  const [providerDetected, setProviderDetected] = useState(false);
  
  // Audio playback refs
  const isProcessingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const shouldContinueRef = useRef(false);
  const currentMessageIndexRef = useRef(0);

  // WebSocket streaming refs (for progressive playback with ElevenLabs)
  const wsClientRef = useRef(null);

  const socket = useSocket();

  // Streaming TTS is relay-only (key-removal plan Phase 3 — no ElevenLabs key in
  // the bundle). Stream when the socket is available; otherwise fall through to
  // the server-side REST /tts/generate route below.
  const useRelayTransport = !!socket;

  // Detect TTS provider for optimization
  useEffect(() => {
    const detectProvider = async () => {
      if (!token) {
        setTtsProvider('elevenlabs'); // Default to ElevenLabs
        setProviderDetected(true);
        return;
      }

      try {
        const response = await api.get('/tts/provider-info', token, {
          params: { interface: 'therapist_review' }
        });

        const provider = response?.data?.provider?.toLowerCase() || 'elevenlabs';
        setTtsProvider(provider);
      } catch (error) {
        console.error('[ConversationAutoplay] Failed to detect provider:', error);
        setTtsProvider('elevenlabs'); // Default to ElevenLabs
      } finally {
        setProviderDetected(true);
      }
    };

    detectProvider();
  }, [token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup WebSocket
      if (wsClientRef.current) {
        wsClientRef.current.stop();
        wsClientRef.current = null;
      }
    };
  }, []);

  // Filter messages for playback
  const getPlayableMessages = useCallback(() => {
    return messages.filter(msg => {
      if (!msg.text || !msg.text.trim()) return false;
      if (skipSystemMessages && msg.senderRole === 'system') return false;
      return true;
    });
  }, [messages, skipSystemMessages]);

  // Get voice for message
  const getVoiceId = useCallback((message) => {
    return getVoiceForMessage(message, userSettings, platformSettings, therapistSettings, clientId);
  }, [userSettings, platformSettings, therapistSettings, clientId]);

  // Preprocess text for TTS
  const preprocessText = useCallback((text) => {
    if (!text) return '';
    
    if (containsMarkdown(text) && platformSettings?.markdownToSSMLEnabled !== false) {
      return markdownToSSML(text, {
        headerPause: '0.5s',
        listItemPause: '0.3s',
        paragraphPause: '0.4s',
        enableEmphasis: true
      });
    }
    
    return text;
  }, [platformSettings]);

  // Generate audio for a message
  const generateAudio = useCallback(async (message) => {
    const voiceId = getVoiceId(message);

    // Skip if no voice ID available
    if (!voiceId) {
      return null;
    }

    abortControllerRef.current = new AbortController();

    try {
      // Stream via the server relay when the socket is available; a socketless
      // moment skips streaming so playback falls through to the server-side REST
      // /tts/generate route below — degrades cleanly, no key in the bundle.
      if (token && ttsProvider === 'elevenlabs' && useRelayTransport) {
        const startTime = performance.now();

        // Use plain text for WebSocket (WebSocket API doesn't support SSML)
        const plainText = message.text;

        return new Promise((resolve, reject) => {
          // Cleanup previous WebSocket if exists
          // This is safe because playMessage has a guard (isProcessingRef.current)
          // that prevents calling generateAudio while another message is playing
          if (wsClientRef.current) {
            wsClientRef.current.stop();
            wsClientRef.current = null;
          }

          // Create the streaming client with progressive playback (same as
          // TextToSpeech) — relay via the server socket, no key in the bundle.
          wsClientRef.current = new SocketTTSStream(
            socket,
            voiceId,
            platformSettings?.ttsModel || 'eleven_turbo_v2_5',
            true,   // useProgressivePlayback
            false,  // disableInternalPlayback - client plays internally
            { role: 'ai_therapist', interface: 'therapist_review' }
          );

          // Capture the message index for this specific WebSocket instance
          const messageIndexForThisWS = currentMessageIndexRef.current;

          // Set up callbacks - WebSocket handles playback internally
          wsClientRef.current.setCallbacks({
            onPlaybackEnd: () => {
              // Only process if this is still the current message
              if (currentMessageIndexRef.current !== messageIndexForThisWS) {
                return;
              }

              // Message finished playing, move to next
              const endTime = performance.now();

              isProcessingRef.current = false;

              if (shouldContinueRef.current) {
                setTimeout(() => {
                  if (shouldContinueRef.current && playMessageRef.current) {
                    playMessageRef.current(currentMessageIndexRef.current + 1);
                  }
                }, pauseBetweenMessages);
              }
            },
            onError: (error) => {
              // Only process if this is still the current message
              if (currentMessageIndexRef.current !== messageIndexForThisWS) {
                return;
              }

              console.error('[ConversationAutoplay] WebSocket error:', error);
              isProcessingRef.current = false;

              // Skip to next message on error
              if (shouldContinueRef.current && playMessageRef.current) {
                setTimeout(() => {
                  playMessageRef.current(currentMessageIndexRef.current + 1);
                }, pauseBetweenMessages);
              }
            }
          });

          // Start streaming with plain text (WebSocket doesn't support SSML)
          // Don't resolve - let the WebSocket handle playback and call onPlaybackEnd when done
          wsClientRef.current.startStreaming(plainText)
            .then(() => {
              // Note: NOT resolving here - onPlaybackEnd callback will trigger next message
            })
            .catch(reject);
        });
      }
      // Use backend TTS for other providers or if no API key
      else if (token) {
        const startTime = performance.now();


        // Use SSML-processed text for REST API (supports SSML)
        const processedText = preprocessText(message.text);

        const ttsContext = {
          text: processedText,
          voiceId: voiceId,
          role: message.senderRole || 'ai_therapist',
          interface: 'therapist_review',
          clientId: clientId
        };

        const result = await api.post('/tts/generate', ttsContext, token);

        const endTime = performance.now();

        // api.post returns full axios response, so access .data
        if (result?.data && result.data.success && result.data.audio) {
          // Convert base64 to blob
          const byteCharacters = atob(result.data.audio);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const audioBlob = new Blob([byteArray], { type: 'audio/mpeg' });
          return URL.createObjectURL(audioBlob);
        } else {
          throw new Error(result?.data?.msg || 'Failed to generate speech via backend');
        }
      } else {
        // No token = no synthesis. The direct-ElevenLabs fallback that lived
        // here required the API key in the web bundle, and a therapist without
        // an auth token cannot reach this screen anyway (key-removal plan,
        // Phase 1.3).
        throw new Error('Not authenticated — cannot generate speech');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('Error generating audio:', err);
      throw err;
    }
  }, [token, ttsProvider, useRelayTransport, socket, platformSettings, getVoiceId, preprocessText, clientId, pauseBetweenMessages]);

  // Forward declaration of playMessage using ref pattern to avoid circular dependency
  const playMessageRef = useRef(null);

  // Play a single message
  const playMessage = useCallback(async (messageIndex) => {
    const playableMessages = getPlayableMessages();

    // Check if we've reached the end
    if (messageIndex >= playableMessages.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      isProcessingRef.current = false;
      shouldContinueRef.current = false;
      return;
    }

    // Don't process if we're already processing
    if (isProcessingRef.current) {
      return;
    }

    // Check if we should continue
    if (!shouldContinueRef.current) {
      return;
    }

    const message = playableMessages[messageIndex];

    // Update current index in both state and ref
    setCurrentMessageIndex(messageIndex);
    currentMessageIndexRef.current = messageIndex;

    // Update progress based on message index
    const baseProgress = (messageIndex / playableMessages.length) * 100;
    setProgress(baseProgress);

    // Highlight current message
    if (onMessageHighlight && autoScrollToMessage) {
      onMessageHighlight(message._id);
    }

    // Start streaming - WebSocket will handle audio generation and playback
    // The onPlaybackEnd callback will be triggered when this message finishes
    isProcessingRef.current = true;

    generateAudio(message).catch((err) => {
      console.error('Error starting streaming for message:', err);
      isProcessingRef.current = false;
      // Error will also trigger onError callback which will skip to next message
    });

    // Note: isProcessingRef will be set to false in onPlaybackEnd callback
  }, [getPlayableMessages, onMessageHighlight, autoScrollToMessage, generateAudio]);

  // Update the ref whenever playMessage changes
  useEffect(() => {
    playMessageRef.current = playMessage;
  }, [playMessage]);

  // Control handlers
  const handlePlay = useCallback(() => {
    const playableMessages = getPlayableMessages();
    if (playableMessages.length === 0) return;


    // If we're already playing and not paused, don't do anything
    if (isPlaying && !isPaused) return;

    setIsPlaying(true);
    setIsPaused(false);
    shouldContinueRef.current = true;

    // Start playing from current index
    playMessage(currentMessageIndex || 0);
  }, [getPlayableMessages, isPlaying, isPaused, currentMessageIndex, playMessage]);

  const handlePause = useCallback(() => {
    setIsPaused(true);
    shouldContinueRef.current = false;

    // Stop the WebSocket playback
    if (wsClientRef.current) {
      wsClientRef.current.stop();
    }
  }, []);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentMessageIndex(0);
    setProgress(0);
    shouldContinueRef.current = false;

    // Reset processing flag
    isProcessingRef.current = false;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Cleanup WebSocket if active
    if (wsClientRef.current) {
      wsClientRef.current.stop();
      wsClientRef.current = null;
    }
  }, []);

  const handleSkipNext = useCallback(() => {
    const playableMessages = getPlayableMessages();
    const nextIndex = currentMessageIndex + 1;


    if (nextIndex < playableMessages.length) {
      // Stop current WebSocket playback
      if (wsClientRef.current) {
        wsClientRef.current.stop();
      }

      isProcessingRef.current = false;

      if (isPlaying && !isPaused) {
        // If playing, play the next message
        playMessage(nextIndex);
      } else {
        // If not playing, just update the index
        setCurrentMessageIndex(nextIndex);
        const newProgress = (nextIndex / playableMessages.length) * 100;
        setProgress(newProgress);
      }
    }
  }, [currentMessageIndex, getPlayableMessages, isPlaying, isPaused, playMessage]);

  const handleSkipPrevious = useCallback(() => {
    const prevIndex = currentMessageIndex - 1;


    if (prevIndex >= 0) {
      // Stop current WebSocket playback
      if (wsClientRef.current) {
        wsClientRef.current.stop();
      }

      isProcessingRef.current = false;

      if (isPlaying && !isPaused) {
        // If playing, play the previous message
        playMessage(prevIndex);
      } else {
        // If not playing, just update the index
        setCurrentMessageIndex(prevIndex);
        const playableMessages = getPlayableMessages();
        const newProgress = (prevIndex / playableMessages.length) * 100;
        setProgress(newProgress);
      }
    }
  }, [currentMessageIndex, isPlaying, isPaused, playMessage, getPlayableMessages]);

  const handleSeek = useCallback((event, newValue) => {
    const playableMessages = getPlayableMessages();
    const newIndex = Math.floor((newValue / 100) * playableMessages.length);

    // Ensure valid index
    const validIndex = Math.max(0, Math.min(newIndex, playableMessages.length - 1));


    // Stop current WebSocket playback
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (wsClientRef.current) {
      wsClientRef.current.stop();
    }

    // Reset processing flag
    isProcessingRef.current = false;

    setCurrentMessageIndex(validIndex);
    setProgress(newValue);

    if (isPlaying && !isPaused && shouldContinueRef.current) {
      // Play from the new position
      playMessage(validIndex);
    }
  }, [getPlayableMessages, isPlaying, isPaused, playMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleStop();
    };
  }, []);

  const playableMessages = getPlayableMessages();
  const currentMessage = playableMessages[currentMessageIndex];

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1200,
        borderRadius: 2,
        overflow: 'visible',
        transition: 'all 0.3s ease',
        width: isExpanded ? 400 : 320
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          position: 'relative',
          zIndex: 10,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VolumeUpIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle2">
            Conversation Autoplay
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }}
            aria-label={isExpanded ? 'Collapse autoplay' : 'Expand autoplay'}
            sx={{
              color: 'inherit',
              pointerEvents: 'auto',
              zIndex: 2
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleStop();
              if (onClose) {
                onClose();
              }
            }}
            aria-label="Close autoplay"
            sx={{
              color: 'inherit',
              ml: 0.5,
              pointerEvents: 'auto',
              zIndex: 2,
              cursor: 'pointer'
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Controls */}
      <Box sx={{
        p: 2,
        position: 'relative',
        zIndex: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <IconButton
            onClick={handleSkipPrevious}
            disabled={currentMessageIndex === 0 || playableMessages.length === 0}
            aria-label="Skip previous"
          >
            <SkipPreviousIcon />
          </IconButton>
          
          {isPlaying && !isPaused ? (
            <IconButton
              onClick={handlePause}
              color="primary"
              aria-label="Pause"
              sx={{ mx: 1 }}
            >
              <PauseIcon fontSize="large" />
            </IconButton>
          ) : (
            <IconButton
              onClick={handlePlay}
              color="primary"
              disabled={playableMessages.length === 0}
              aria-label="Play"
              sx={{ mx: 1 }}
            >
              <PlayArrowIcon fontSize="large" />
            </IconButton>
          )}
          
          <IconButton
            onClick={handleStop}
            disabled={!isPlaying && currentMessageIndex === 0}
            aria-label="Stop"
          >
            <StopIcon />
          </IconButton>
          
          <IconButton
            onClick={handleSkipNext}
            disabled={currentMessageIndex >= playableMessages.length - 1}
            aria-label="Skip next"
          >
            <SkipNextIcon />
          </IconButton>
          
          <IconButton
            onClick={(e) => setSettingsAnchor(e.currentTarget)}
            aria-label="Settings"
            sx={{ ml: 1 }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption">
              Message {currentMessageIndex + 1} of {playableMessages.length}
            </Typography>
            <Typography variant="caption">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <Slider
            value={progress}
            onChange={handleSeek}
            disabled={playableMessages.length === 0}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value)}%`}
          />
        </Box>

        {/* Current Message Info */}
        {isExpanded && currentMessage && (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}
          >
            <Typography variant="caption" color="text.secondary">
              Current: {currentMessage.senderRole === 'ai' ? 'AI Therapist' :
                       currentMessage.senderRole === 'client' ? 'Client' :
                       currentMessage.senderRole === 'human_therapist' ? 'Therapist' :
                       'System'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
              {currentMessage.text}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
      >
        <MenuItem>
          <ListItemIcon>
            <TimerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            Pause between messages: {pauseBetweenMessages / 1000}s
          </ListItemText>
        </MenuItem>
        <Box sx={{ px: 2, py: 1 }}>
          <Slider
            value={pauseBetweenMessages}
            onChange={(e, v) => setPauseBetweenMessages(v)}
            min={500}
            max={3000}
            step={500}
            size="small"
          />
        </Box>
        <Divider />
        <MenuItem>
          <FormControlLabel
            control={
              <Switch
                checked={skipSystemMessages}
                onChange={(e) => setSkipSystemMessages(e.target.checked)}
                size="small"
              />
            }
            label="Skip system messages"
          />
        </MenuItem>
        <MenuItem>
          <FormControlLabel
            control={
              <Switch
                checked={autoScrollToMessage}
                onChange={(e) => setAutoScrollToMessage(e.target.checked)}
                size="small"
              />
            }
            label="Auto-scroll to message"
          />
        </MenuItem>
        {!hideExportOption && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                setShowExportDialog(true);
                setSettingsAnchor(null);
              }}
            >
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export Audio</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Audio Export Dialog */}
      {!hideExportOption && showExportDialog && (
        <AudioExportDialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          conversationId={conversationId}
          messageCount={messages?.length || 0}
        />
      )}
    </Paper>
  );
};

export default ConversationAutoplay;